import logging from "utils/logging";
import {
  createOpenListenerAction,
  createCloseListenerAction,
} from "../../redux/actions";
import Provider from "provider";
import * as Constants from "features/gamestate/constants";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { computeDiff } from "../../utils/diffUtils";

const log = logging.getLogger("gameStateSlice");

const gameStateSlice = createSlice({
  name: "game",
  initialState: { game: null, loading: false, gameStateChanging: false },
  reducers: {
    gameUpdated(state, action) {
      state.game = action.payload.game;
      state.gameStateChanging = false;
      state.loading = false;
    },
    loadingGame(state) {
      state.loading = true;
    },
    updatingGameState(state) {
      state.gameStateChanging = true;
    },
  },
});

const watchGameActionBase = createOpenListenerAction(
  "game/watch_game",
  "watch-game"
);

const stopWatchingGameActionBase = createCloseListenerAction(
  "game/stop_watching_game",
  "watch-game"
);

const watchGameAction = (gameId) => (dispatch) => {
  dispatch(gameStateSlice.actions.loadingGame());
  dispatch(
    watchGameActionBase({ gameId }, async () => {
      log.info("Watching game: ", gameId);
      let previousGameState = null;
      let listener = await Provider.watchGame(gameId, (gameUpdate) => {
        // Only dispatch if something meaningful has changed
        const hasChanged = previousGameState === null || 
                          JSON.stringify(previousGameState) !== JSON.stringify(gameUpdate);
        
        if (hasChanged) {
          // Compute and log what changed
          const changes = computeDiff(previousGameState, gameUpdate, { rootPath: 'game' });
          log.debug("Game update changes:", changes);
          
          dispatch(gameStateSlice.actions.gameUpdated({ game: gameUpdate }));
          previousGameState = { ...gameUpdate };
        } else {
          log.debug("Ignoring game update with no meaningful changes");
        }
      });
      return listener;
    })
  );
};

const stopWatchingGameAction = (gameId) => (dispatch) => {
  dispatch(stopWatchingGameActionBase({ gameId }));
};

const watchRoomAction = (roomId) => (dispatch) => {
  dispatch(gameStateSlice.actions.loadingGame());
  dispatch(
    watchGameActionBase({ roomId }, async () => {
      log.info("Watching room: ", roomId);
      let previousGameState = null;
      let listener = await Provider.watchGameForRoomId(roomId, (gameUpdate) => {
        // Only dispatch if something meaningful has changed
        log.debug("Room game update: ", gameUpdate);
        const hasChanged = previousGameState === null || 
                          JSON.stringify(previousGameState) !== JSON.stringify(gameUpdate);
        
        if (hasChanged) {
          // Compute and log what changed
          const changes = computeDiff(previousGameState, gameUpdate, { rootPath: 'game' });
          log.debug("Room game update changes:", changes);
          
          dispatch(gameStateSlice.actions.gameUpdated({ game: gameUpdate }));
          previousGameState = { ...gameUpdate };
        } else {
          log.debug("Ignoring room game update with no meaningful changes");
        }
      });
      return listener;
    })
  );
};

const stopWatchingRoomAction = (roomId) => (dispatch) => {
  dispatch(stopWatchingGameActionBase({ roomId }));
};

const updatingGameState = gameStateSlice.actions.updatingGameState;

export {
  watchGameAction,
  stopWatchingGameAction,
  watchRoomAction,
  stopWatchingRoomAction,
  updatingGameState,
};

/* -- Selectors -- */
const selectGameState = (state) => state.game;

const selectLoadingGame = createSelector(selectGameState, (gs) => gs.loading);
const selectGameObject = createSelector(selectGameState, (gs) => gs.game);
const selectGameId = createSelector(selectGameObject, (g) => (g ? g.id : null));

const selectGameStatusObject = createSelector(selectGameState, (gs) =>
  gs.game !== null ? gs.game.state : {}
);

export const getSimpleGameStateForGame = (gameObject) => {
  const { state: gameState } = gameObject;
  switch (gameState.currentState) {
    case Constants.GAMESTATE_LOBBY:
      if (!gameState.round) {
        // This is a new game.
        return "Not Started";
      }
      return "In Progress";
    case Constants.GAMESTATE_ROUND:
      return "In Progress";
    case Constants.GAMESTATE_CREDITS:
      return "Finished";
    default:
      return "";
  }
};

const selectFinalRevealState = createSelector(
  selectGameStatusObject,
  (gameStatus) => gameStatus?.finalReveal || null
);

export {
  selectGameState,
  selectLoadingGame,
  selectGameObject,
  selectGameId,
  selectGameStatusObject,
  selectFinalRevealState,
};

export default gameStateSlice.reducer;
